import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const Data = () => {
    const navigate = useNavigate();

    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortOrderInactive, setSortOrderInactive] = useState('desc');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://wellness.nextriking.com/api/dashboard/all');
            setDashboardData(response.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Error al cargar los datos');
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/dashboard');
    };

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '20px' }}>
                <div style={{ textAlign: 'center', padding: '50px', fontSize: '1.2rem', color: '#666' }}>
                    Cargando datos...
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '20px' }}>
                <div style={{ textAlign: 'center', padding: '50px', fontSize: '1.2rem', color: '#e03131' }}>
                    {error}
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return null;
    }

    const { kpis, trend, heatmap, debtors, hourly, inactive } = dashboardData;

    // Calcular "Pagaron pero no vienen" (7+ días inactivos, no deudores)
    let paidInactive = inactive?.filter(user => {
        const isDebtor = debtors.some(debtor => debtor.id === user.id);
        return !isDebtor;
    }) || [];

    // Ordenar por días inactivos
    paidInactive = paidInactive.sort((a, b) => {
        if (sortOrderInactive === 'desc') {
            return b.days_inactive - a.days_inactive; // Mayor a menor
        } else {
            return a.days_inactive - b.days_inactive; // Menor a mayor
        }
    });
    // Procesar datos del heatmap
    const heatmapByHour = {};
    heatmap.forEach(item => {
        if (!heatmapByHour[item.hour]) {
            heatmapByHour[item.hour] = [];
        }
        heatmapByHour[item.hour].push(item);
    });

    // Agrupar datos por hora para el gráfico
    const hourlyData = Array.from({ length: 24 }, (_, i) => {
        const hourData = hourly.filter(h => h.hour === i);
        const total = hourData.reduce((sum, h) => sum + h.total_checkins, 0);
        return {
            hour: `${i}:00`,
            checkins: total
        };
    }).filter(h => h.checkins > 0);


    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '20px'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#4472c4',
                color: 'white',
                padding: '20px',
                borderRadius: '8px 8px 0 0',
                marginBottom: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                    }}>
                        📊
                    </div>
                    <h2 style={{ margin: 0, fontSize: '24px' }}>
                        Dashboard del Gimnasio
                    </h2>
                </div>
                <button
                    onClick={handleBack}
                    style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                    ← Volver atrás
                </button>
            </div>

            {/* Contenedor principal */}
            <div style={{
                backgroundColor: 'white',
                padding: '30px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                maxWidth: '1400px',
                margin: '0 auto'
            }}>

                {/* Sección Estadísticas */}
                <div style={{ marginBottom: '30px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '20px'
                    }}>
                        <span style={{ fontSize: '24px' }}>📊</span>
                        <h3 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Estadísticas</h3>
                    </div>

                    {/* KPIs Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '20px',
                        marginBottom: '30px'
                    }}>
                        <div style={{
                            border: '3px solid #6c757d',
                            borderRadius: '12px',
                            padding: '25px',
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '10px' }}>👥</div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                Total Miembros
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#6c757d' }}>
                                {kpis.total_members}
                            </div>
                        </div>

                        <div style={{
                            border: '3px solid #28a745',
                            borderRadius: '12px',
                            padding: '25px',
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏃</div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                Activos Esta Semana
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
                                {kpis.active_this_week}
                            </div>
                        </div>

                        <div style={{
                            border: '3px solid #ffc107',
                            borderRadius: '12px',
                            padding: '25px',
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚠️</div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                Pagaron pero No Vienen
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffc107' }}>
                                {paidInactive.length}
                            </div>
                        </div>

                        <div style={{
                            border: '3px solid #dc3545',
                            borderRadius: '12px',
                            padding: '25px',
                            textAlign: 'center',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⭐</div>
                            <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                Promedio Diario
                            </div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc3545' }}>
                                {Math.round(kpis.avg_daily_checkins)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Gráfico de Tendencia */}
                <div style={{ marginBottom: '40px' }}>
                    <h3 style={{ fontSize: '18px', color: '#333', marginBottom: '20px' }}>
                        📈 Tendencia de Asistencia (Últimos 30 días)
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={trend}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(date) => new Date(date).toLocaleDateString('es-AR', { month: 'short', day: 'numeric' })}
                            />
                            <YAxis />
                            <Tooltip
                                labelFormatter={(date) => new Date(date).toLocaleDateString('es-AR')}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="unique_users" stroke="#28a745" name="Usuarios Únicos" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico de Horarios Pico */}
                <div style={{ marginBottom: '40px' }}>
                    <h3 style={{ fontSize: '18px', color: '#333', marginBottom: '20px' }}>
                        ⏰ Horarios Pico
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={hourlyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="checkins" fill="#4472c4" name="Check-ins" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Heatmap */}
                <div style={{ marginBottom: '40px' }}>
                    <h3 style={{ fontSize: '18px', color: '#333', marginBottom: '20px' }}>
                        🔥 Mapa de Calor - Afluencia por Hora y Día
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: '600px' }}>
                            {/* Header del heatmap */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '80px repeat(6, 80px)',
                                gap: '2px',
                                marginBottom: '2px'
                            }}>
                                <div style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    backgroundColor: '#f8f9fa',
                                    color: '#495057',
                                    borderRadius: '4px'
                                }}></div>
                                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                                    <div key={day} style={{
                                        padding: '12px',
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        backgroundColor: '#4472c4',
                                        color: 'white',
                                        borderRadius: '4px'
                                    }}>
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Filas del heatmap */}
                            {Object.entries(heatmapByHour).map(([hour, data]) => {
                                const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                return (
                                    <div key={hour} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '80px repeat(6, 80px)',
                                        gap: '2px',
                                        marginBottom: '2px'
                                    }}>
                                        <div style={{
                                            padding: '12px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            backgroundColor: '#f8f9fa',
                                            color: '#495057',
                                            borderRadius: '4px'
                                        }}>
                                            {hour}:00
                                        </div>
                                        {dayNames.map(dayName => {
                                            const dayData = data.find(d => d.day_name === dayName);
                                            const intensity = dayData ? Math.min(dayData.checkins / 80, 1) : 0;
                                            return (
                                                <div
                                                    key={dayName}
                                                    style={{
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        backgroundColor: `rgba(68, 114, 196, ${intensity})`,
                                                        color: intensity > 0.5 ? 'white' : 'black',
                                                        borderRadius: '4px',
                                                        border: '1px solid #e9ecef',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.2s'
                                                    }}
                                                    title={`${dayData?.checkins || 0} check-ins`}
                                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    {dayData?.checkins || 0}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Tabla de Deudores */}
                <div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '20px'
                    }}>
                        <span style={{ fontSize: '24px' }}>💰</span>
                        <h3 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
                            Usuarios Deudores ({debtors.length})
                        </h3>
                    </div>

                    <div style={{
                        overflowX: 'auto',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                        }}>
                            <thead style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10
                            }}>
                                <tr style={{ backgroundColor: '#4472c4' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>
                                        Nombre
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>
                                        DNI
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>
                                        Email
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>
                                        Teléfono
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>
                                        Días desde pago
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>
                                        Sesiones
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {debtors.map((debtor, index) => (
                                    <tr
                                        key={debtor.id}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                                            borderBottom: '1px solid #dee2e6'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}
                                    >
                                        <td style={{ padding: '12px' }}>
                                            {debtor.name} {debtor.lastname}
                                        </td>
                                        <td style={{ padding: '12px' }}>{debtor.dni}</td>
                                        <td style={{ padding: '12px' }}>{debtor.email || 'N/A'}</td>
                                        <td style={{ padding: '12px' }}>{debtor.phone || 'N/A'}</td>
                                        <td style={{ padding: '12px', color: '#dc3545', fontWeight: 'bold' }}>
                                            {debtor.days_since_payment} días
                                        </td>
                                        <td style={{ padding: '12px' }}>{debtor.total_sessions}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Tabla de Pagaron pero No Vienen */}
                <div style={{ marginTop: '40px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '20px'
                    }}>
                        <span style={{ fontSize: '24px' }}>⚠️</span>
                        <h3 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
                            Pagaron pero Dejaron de Venir ({paidInactive.length})
                        </h3>
                    </div>

                    <div style={{
                        overflowX: 'auto',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '14px'
                        }}>
                            <thead style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10
                            }}>
                                <tr style={{ backgroundColor: '#ffc107' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        Nombre
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        Teléfono
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        Último Pago
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                        Última Visita
                                    </th>
                                    <th
                                        style={{
                                            padding: '12px',
                                            textAlign: 'left',
                                            color: 'white',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onClick={() => setSortOrderInactive(sortOrderInactive === 'desc' ? 'asc' : 'desc')}
                                    >
                                        Días sin venir {sortOrderInactive === 'desc' ? '↓' : '↑'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paidInactive.length > 0 ? (
                                    paidInactive.map((user, index) => (
                                        <tr
                                            key={user.id}
                                            style={{
                                                backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                                                borderBottom: '1px solid #dee2e6'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}
                                        >
                                            <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                                {user.name} {user.lastname}
                                            </td>
                                            <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                                {user.phone || 'N/A'}
                                            </td>
                                            <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                                {user.lastPayment ? new Date(user.lastPayment).toLocaleDateString('es-AR') : 'N/A'}
                                            </td>
                                            <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                                {user.last_checkin ? new Date(user.last_checkin).toLocaleDateString('es-AR') : 'Nunca'}
                                            </td>
                                            <td style={{ padding: '12px', color: '#ffc107', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                                {user.days_inactive !== null && user.days_inactive !== undefined
                                                    ? `${user.days_inactive} días`
                                                    : 'Nunca asistió'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                            ¡Excelente! No hay usuarios en esta categoría
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    textAlign: 'center',
                    color: '#868e96',
                    fontSize: '0.9rem',
                    marginTop: '30px',
                    padding: '10px'
                }}>
                    Última actualización: {new Date(dashboardData.lastUpdate).toLocaleString('es-AR')}
                </div>
            </div>
        </div >
    );
};

export default Data;
